import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  User,
  Phone,
  Globe,
  MapPin,
  Home,
  Clock,
  History,
  StickyNote,
  Pencil,
  CalendarPlus,
  UserPlus,
  UserCog,
  Users,
  Ban,
  AlarmClock,
  Check,
  Loader2,
} from 'lucide-react';
import { EditClientDialog } from './EditClientDialog';
import { PoolDelegateDialog } from './PoolDelegateDialog';
import { CloseSellerDialog } from './CloseSellerDialog';
import { CreateAppointmentDialog } from '@/components/appointments/CreateAppointmentDialog';
import { CreatePropertyDialog } from '@/components/properties/CreatePropertyDialog';
import { ViewPropertyDialog } from '@/components/properties/ViewPropertyDialog';
import { useProperty } from '@/hooks/data/useProperties';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar } from '@/components/ui/avatar';
import {
  type ClientHistoryRow,
  type ClientRow,
  useAssignSecondaryAgent,
  useClaimPoolClient,
  useRemoveSecondaryAgent,
  useReopenClient,
  useClient,
  useClientAppointments,
  useClientContactAgents,
  useClientExternalInterests,
  useClientHistory,
  useClientProperties,
  useClientTimelineNotes,
  useCreateTimelineNote,
  useDeleteTimelineNote,
  useCompleteClientAction,
  useSetClientReminder,
} from '@/hooks/data/useClients';
import { useAgents } from '@/hooks/data/useAgents';
import { fieldLabel, valueLabel } from '@/lib/historyLabels';
import { useAuth } from '@/hooks/useAuth';
import { humanizeApiError } from '@/lib/errorMessages';
import { AssignSecondaryAgentDialog } from './AssignSecondaryAgentDialog';
import { toast } from 'sonner';
import { formatDate, formatDateTime, formatMoney, formatNumber } from '@/lib/formatLocale';

interface Props {
  client: ClientRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function KundendetailsDialog({ client: seed, open, onOpenChange }: Props) {
  const { t } = useTranslation();
  // Fresh fetch — list view may be stale.
  const { data: fresh } = useClient(open ? seed.id : undefined);
  const client = fresh ?? seed;
  const [editing, setEditing] = useState(false);
  // Action sub-dialogs. Opening from the client detail passes the client
  // as a *locked* selection, so it works even for imported (Abgeber)
  // clients that the audience-filtered pickers would not list.
  const [booking, setBooking] = useState(false);
  const [addingProperty, setAddingProperty] = useState(false);
  // Set when a property is created from here, so the follow-up appointment
  // dialog can pre-select it (property → appointment chain).
  const [createdPropertyId, setCreatedPropertyId] = useState<string | null>(null);

  const clientFullName =
    [client.title, client.first_name, client.last_name].filter(Boolean).join(' ') || '—';
  const isSeller =
    client.client_type === 'prodavac' || client.client_type === 'najmodavac';
  // Sensible default appointment type (operator can still change it in the
  // dialog): buyer-side clients → Besichtigung, sellers → Aufnahme.
  const apptType: 'aufnahme' | 'besichtigung' =
    client.client_type === 'kupac' ||
    client.client_type === 'najmoprimac' ||
    client.client_type === 'interessent'
      ? 'besichtigung'
      : 'aufnahme';

  return (
    <>
    <Dialog open={open && !editing && !booking && !addingProperty} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader className="flex flex-row items-center justify-between gap-3 pe-8">
          <DialogTitle className="flex items-center gap-2 font-display">
            <User className="h-4 w-4" />
            {t('clients.details.title', 'Kundendetails')}
          </DialogTitle>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-line bg-card px-3 py-1.5 text-xs font-semibold text-ink shadow-soft transition hover:bg-paper-2"
          >
            <Pencil className="h-3.5 w-3.5" />
            {t('clients.actions.edit', 'Bearbeiten')}
          </button>
        </DialogHeader>

        {/* Quick actions — book an appointment or (for sellers) add a
            property directly from the client record. Both pass the client
            as a locked selection so the audience-filtered picker is bypassed
            (imported Abgeber leads work here even though they are hidden in
            the global pickers). */}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              // Plain "Neuer Termin" must never carry a property from a
              // prior add-property chain.
              setCreatedPropertyId(null);
              setBooking(true);
            }}
            className="inline-flex items-center gap-1.5 rounded-md border border-line bg-card px-3 py-1.5 text-xs font-semibold text-ink shadow-soft transition hover:bg-paper-2"
          >
            <CalendarPlus className="h-3.5 w-3.5" />
            {t('clients.actions.new_appointment', 'Neuer Termin')}
          </button>
          {isSeller && (
            <button
              type="button"
              onClick={() => setAddingProperty(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-line bg-card px-3 py-1.5 text-xs font-semibold text-ink shadow-soft transition hover:bg-paper-2"
            >
              <Home className="h-3.5 w-3.5" />
              {t('clients.actions.add_property', 'Immobilie hinzufügen')}
            </button>
          )}
        </div>

        {client.do_not_call === true && (
          <p className="flex items-start gap-2 rounded-md border border-[hsl(var(--danger))]/30 bg-[hsl(var(--danger-bg))] px-3 py-2 text-xs text-[hsl(var(--danger))]">
            <Ban className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              <span className="font-semibold">{t('clients.do_not_call.badge', 'Werbung untersagt (DSGVO)')}</span>
              {' — '}
              {t(
                'clients.do_not_call.blocked',
                'Dieser Klient hat Werbung untersagt (DSGVO-Widerspruch). Aus dem Kundendatensatz geht keine SMS, WhatsApp oder E-Mail an ihn.',
              )}
            </span>
          </p>
        )}

        {(client.platform ?? '').startsWith('Kundenpool') && !client.assigned_agent_id ? (
          <PoolClaimBanner client={client} />
        ) : (
          <LockBanner client={client} />
        )}

        <Tabs defaultValue="details" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="details">{t('clients.details.tab_details', 'Details')}</TabsTrigger>
            <TabsTrigger value="related">
              {t('clients.details.tab_related', 'Zugehörig')}
            </TabsTrigger>
            <TabsTrigger value="notes">
              {t('clients.details.tab_notes', 'Notizen')}
            </TabsTrigger>
            <TabsTrigger value="history">
              {t('clients.details.tab_history', 'Verlauf')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="mt-4">
            <DetailsPanel client={client} />
          </TabsContent>
          <TabsContent value="related" className="mt-4">
            <RelatedPanel client={client} />
          </TabsContent>
          <TabsContent value="notes" className="mt-4">
            <NotesPanel client={client} />
          </TabsContent>
          <TabsContent value="history" className="mt-4">
            <HistoryPanel client={client} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
    {editing && (
      <EditClientDialog
        client={client}
        open={editing}
        onOpenChange={(o) => setEditing(o)}
      />
    )}
    {booking && (
      <CreateAppointmentDialog
        open
        onOpenChange={(o) => {
          setBooking(o);
          if (!o) setCreatedPropertyId(null);
        }}
        defaultType={apptType}
        defaultPropertyId={createdPropertyId ?? undefined}
        lockedClient={{
          id: client.id,
          label: clientFullName,
          secondary: client.phone ?? undefined,
          address: client.address,
          location: client.location,
        }}
      />
    )}
    {addingProperty && isSeller && (
      <CreatePropertyDialog
        open
        onOpenChange={(o) => setAddingProperty(o)}
        lockedSeller={{
          id: client.id,
          label: clientFullName,
          secondary: client.phone ?? undefined,
          clientType: client.client_type as 'prodavac' | 'najmodavac',
        }}
        onCreated={(propertyId) => {
          // Chain: property created → open appointment pre-filled with it.
          setCreatedPropertyId(propertyId);
          setBooking(true);
        }}
      />
    )}
    </>
  );
}

// ─────────── Tab 1: Details ───────────

// Private follow-up reminder ("Anruf fällig") the makler sets herself. Writes
// clients.next_action/_type/_due via PATCH — which sends NO SMS/e-mail to the
// client (unlike an appointment) — and shows up under "Anstehende Aktionen" on
// the dashboard. Cleared with the same "Erledigt" path as the dashboard.
// Added for Susanne Kirsch (01.10.2026): she could only get these via Zoki.
function ReminderCard({ client }: { client: ClientRow }) {
  const { t } = useTranslation();
  const setReminder = useSetClientReminder();
  const complete = useCompleteClientAction();

  // next_action_due is typed number but arrives as an ISO string over JSON
  // (documented project pitfall) — parse defensively.
  const rawDue = client.next_action_due as number | string | null;
  const dueMs =
    rawDue == null ? null : typeof rawDue === 'number' ? rawDue : new Date(rawDue).getTime();
  const hasReminder = Boolean(client.next_action && dueMs != null);

  const [editing, setEditing] = useState(false);
  const [date, setDate] = useState('');
  const [text, setText] = useState('');

  const openForm = () => {
    const base =
      dueMs != null
        ? new Date(dueMs)
        : (() => {
            const d = new Date();
            d.setDate(d.getDate() + 1);
            return d;
          })();
    const y = base.getFullYear();
    const m = String(base.getMonth() + 1).padStart(2, '0');
    const d = String(base.getDate()).padStart(2, '0');
    setDate(`${y}-${m}-${d}`);
    setText(client.next_action ?? t('clients.reminder.default_action', 'Anrufen'));
    setEditing(true);
  };

  const save = () => {
    if (!date) return;
    const [y, m, d] = date.split('-').map(Number);
    const due = new Date(y!, (m ?? 1) - 1, d ?? 1, 9, 0, 0, 0).getTime();
    setReminder.mutate(
      {
        id: client.id,
        action: text.trim() || t('clients.reminder.default_action', 'Anrufen'),
        dueMs: due,
      },
      {
        onSuccess: () => {
          toast.success(t('clients.reminder.saved', 'Erinnerung gesetzt.'));
          setEditing(false);
        },
        onError: (e) =>
          toast.error(
            humanizeApiError(
              (e as Error).message,
              t,
              t('clients.reminder.failed', 'Konnte nicht gespeichert werden. Bitte erneut versuchen.'),
            ),
          ),
      },
    );
  };

  const clear = () =>
    complete.mutate(client.id, {
      onSuccess: () => toast.success(t('clients.reminder.cleared', 'Als erledigt markiert.')),
      onError: (e) =>
        toast.error(
          humanizeApiError(
            (e as Error).message,
            t,
            t('clients.reminder.failed', 'Konnte nicht gespeichert werden. Bitte erneut versuchen.'),
          ),
        ),
    });

  return (
    <div className="fm-card border-s-[3px] border-s-[hsl(var(--brand-gold))] p-4">
      <div className="flex items-center gap-2 text-ink">
        <AlarmClock className="h-4 w-4 text-[hsl(var(--brand-gold-deep))]" />
        <p className="font-display text-base">{t('clients.reminder.title', 'Erinnerung')}</p>
      </div>

      {!editing && hasReminder && (
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-ink-2">
            <span className="font-medium text-ink">{client.next_action}</span>
            {dueMs != null && (
              <span className="text-ink-3">
                {' · '}
                {t('clients.reminder.due', 'fällig {{date}}', { date: formatDate(new Date(dueMs)) })}
              </span>
            )}
          </p>
          <div className="flex flex-shrink-0 gap-2">
            <button
              type="button"
              onClick={openForm}
              className="inline-flex items-center gap-1.5 rounded-md border border-line bg-card px-2.5 py-1.5 text-xs font-medium text-ink-2 hover:bg-paper-2"
            >
              <Pencil className="h-3.5 w-3.5" />
              {t('clients.reminder.change', 'Ändern')}
            </button>
            <button
              type="button"
              onClick={clear}
              disabled={complete.isPending}
              className="inline-flex items-center gap-1.5 rounded-md border border-line bg-card px-2.5 py-1.5 text-xs font-semibold text-ink shadow-soft transition hover:border-[hsl(var(--positive))] hover:text-[hsl(var(--positive))] disabled:opacity-50"
            >
              {complete.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5" />
              )}
              {t('clients.reminder.done', 'Erledigt')}
            </button>
          </div>
        </div>
      )}

      {!editing && !hasReminder && (
        <div className="mt-2 flex items-center justify-between gap-2">
          <p className="text-sm text-ink-3">
            {t('clients.reminder.none', 'Keine Erinnerung gesetzt.')}
          </p>
          <button
            type="button"
            onClick={openForm}
            className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-md bg-[hsl(var(--brand-gold))] px-3 py-1.5 text-xs font-semibold text-[#1a1305] shadow-soft transition hover:bg-[hsl(var(--brand-gold-deep))] hover:text-white"
          >
            <AlarmClock className="h-3.5 w-3.5" />
            {t('clients.reminder.set', 'Erinnerung setzen')}
          </button>
        </div>
      )}

      {editing && (
        <div className="mt-3 space-y-2">
          <div className="grid gap-2 sm:grid-cols-[180px_1fr]">
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-2">
                {t('clients.reminder.date_label', 'Fällig am')}
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full rounded-md border border-line bg-card px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-2">
                {t('clients.reminder.text_label', 'Notiz')}
              </label>
              <input
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={t('clients.reminder.text_ph', 'z. B. Anrufen')}
                className="w-full rounded-md border border-line bg-card px-3 py-2 text-sm"
              />
            </div>
          </div>
          <p className="text-[11px] text-ink-3">
            {t(
              'clients.reminder.hint',
              'Nur für Sie — es wird keine SMS oder E-Mail an den Klienten gesendet. Erscheint in „Anstehende Aktionen" auf Ihrem Dashboard.',
            )}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={save}
              disabled={setReminder.isPending || !date}
              className="inline-flex items-center gap-1.5 rounded-md bg-ink px-3 py-2 text-sm font-semibold text-paper shadow-soft transition hover:bg-ink/90 disabled:opacity-50"
            >
              {setReminder.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              {t('clients.reminder.save', 'Speichern')}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="inline-flex items-center rounded-md border border-line bg-card px-3 py-2 text-sm font-medium text-ink-2 hover:bg-paper-2"
            >
              {t('clients.reminder.cancel', 'Abbrechen')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailsPanel({ client }: { client: ClientRow }) {
  const { t } = useTranslation();
  const fullName = [client.title, client.first_name, client.last_name].filter(Boolean).join(' ');
  return (
    <div className="space-y-4">
      <ReminderCard client={client} />
      <Section title={t('clients.details.basics', 'Grundinformationen')}>
        <Field label={t('clients.fields.fullName', 'Vollständiger Name')} value={fullName} />
        <Field
          label={t('clients.fields.clientType', 'Kundentyp')}
          value={<span className="fm-pill fm-pill-neutral">{t(`enums.client_type.${client.client_type}`, client.client_type)}</span>}
        />
        {client.location && (
          <Field
            label={t('clients.fields.location', 'Standort')}
            value={
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-ink-3" />
                {client.location}
              </span>
            }
          />
        )}
        {client.address && (
          <Field label={t('clients.fields.address', 'Adresse')} value={client.address} />
        )}
        {client.birth_date && (
          <Field
            label={t('clients.fields.birthDate', 'Geburtsdatum')}
            value={formatDate(new Date(client.birth_date))}
          />
        )}
        {client.citizenship && (
          <Field
            label={t('clients.fields.citizenship', 'Staatsbürgerschaft')}
            value={client.citizenship}
          />
        )}
      </Section>

      <Section title={t('clients.details.contact', 'Kontaktinformationen')}>
        <Field
          label={t('clients.fields.phone', 'Telefonnummer 1')}
          value={
            <span className="inline-flex items-center gap-1.5 font-mono">
              <Phone className="h-3.5 w-3.5 text-ink-3" />
              {client.phone}
            </span>
          }
        />
        {client.phone_2 && (
          <Field
            label={t('clients.fields.phone2', 'Telefonnummer 2')}
            value={
              <span className="inline-flex items-center gap-1.5 font-mono">
                <Phone className="h-3.5 w-3.5 text-ink-3" />
                {client.phone_2}
              </span>
            }
          />
        )}
        {client.email && (
          <Field label={t('clients.fields.email', 'E-Mail')} value={client.email} />
        )}
        {client.do_not_call === true && (
          <Field
            label={t('clients.fields.do_not_call', 'Telefonische Werbung untersagt (DSGVO)')}
            value={
              <span className="inline-flex items-center gap-1.5 font-semibold text-[hsl(var(--danger))]">
                <Ban className="h-3.5 w-3.5" />
                {t('clients.do_not_call.badge', 'Werbung untersagt (DSGVO)')}
              </span>
            }
          />
        )}
      </Section>

      {(client.platform || client.platform_code) && (
        <Section title={t('clients.details.platform', 'Plattform-Informationen')}>
          {client.platform && (
            <Field
              label={t('clients.fields.platform', 'Plattform')}
              value={
                <span className="inline-flex items-center gap-1.5">
                  <Globe className="h-3.5 w-3.5 text-ink-3" />
                  {client.platform}
                </span>
              }
            />
          )}
          {client.platform_code && (
            <Field
              label={t('clients.fields.platformCode', 'Plattform-Code')}
              value={<span className="font-mono text-xs">{client.platform_code}</span>}
            />
          )}
        </Section>
      )}

    </div>
  );
}

// ─────────── Tab 2: Zugehörig ───────────

function RelatedPanel({ client }: { client: ClientRow }) {
  const { t, i18n } = useTranslation();
  const { data: properties = [] } = useClientProperties(client.id);
  const { data: appointments = [] } = useClientAppointments(client.id);
  const { data: contactAgents = [] } = useClientContactAgents(client.id);
  const { data: externalInterests = [] } = useClientExternalInterests(client.id);
  // Batch ZO #12 — clicking a property in the related list opens the full
  // ViewPropertyDialog so the operator doesn't have to navigate via the
  // sidebar. The slim ClientPropertyRow doesn't carry all PropertyRow
  // fields, so we fetch the full one via useProperty(id) before render.
  const [viewPropertyId, setViewPropertyId] = useState<string | null>(null);
  const { data: viewProperty } = useProperty(viewPropertyId ?? undefined);

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-line bg-card p-5">
        <h3 className="mb-3 flex items-center gap-2 font-display text-lg text-ink">
          <Home className="h-4 w-4 text-ink-3" />
          {t('clients.related.properties', 'Immobilien')} ({properties.length})
        </h3>
        {properties.length === 0 ? (
          <div className="py-8 text-center">
            <Home className="mx-auto h-10 w-10 text-line-strong" />
            <p className="mt-2 text-sm font-medium text-ink-2">
              {t('clients.related.no_properties', 'Keine Immobilien gefunden')}
            </p>
            <p className="text-xs text-ink-3">
              {t('clients.related.no_properties_hint', 'Dieser Klient hat noch keine Immobilien.')}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {properties.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => setViewPropertyId(p.id)}
                  className="flex w-full items-center gap-3 py-2.5 text-start transition hover:bg-paper-2/50 rounded-md px-2 -mx-2"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-ink truncate">
                      {p.address ?? '—'}
                    </p>
                    <p className="text-xs text-ink-3">
                      {[p.postal_code, p.district].filter(Boolean).join(' · ')}
                      {p.rooms_count ? ` · ${p.rooms_count} Zi` : ''}
                      {p.living_area ? ` · ${p.living_area} m²` : ''}
                    </p>
                  </div>
                  {p.status && (
                    <span className="fm-pill fm-pill-neutral">
                      {t(`enums.marketing_stage.${p.status}`, p.status)}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {viewProperty && viewPropertyId && (
        <ViewPropertyDialog
          property={viewProperty}
          isAdmin={false}
          open={!!viewPropertyId}
          onOpenChange={(o) => !o && setViewPropertyId(null)}
        />
      )}

      <div className="rounded-xl border border-line bg-card p-5">
        <h3 className="mb-3 flex items-center gap-2 font-display text-lg text-ink">
          <Clock className="h-4 w-4 text-ink-3" />
          {t('clients.related.appointments', 'Termine')} ({appointments.length})
        </h3>
        {appointments.length === 0 ? (
          <p className="py-4 text-center text-sm text-ink-3">
            {t('clients.related.no_appointments', 'Keine Termine.')}
          </p>
        ) : (
          <ul className="space-y-2">
            {appointments.map((a) => {
              const d = new Date(a.appointment_date);
              const isUpcoming = d.getTime() > Date.now();
              return (
                <li
                  key={a.id}
                  className="flex items-center gap-3 rounded-md border border-line bg-paper-2 px-3 py-2.5"
                >
                  <Clock className="h-4 w-4 flex-shrink-0 text-ink-3" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-ink">
                      {formatDateTime(d, {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                    <p className="text-[11px] uppercase text-ink-3 tracking-[0.08em]">
                      {t(`enums.appointment_type.${a.appointment_type}`, a.appointment_type)}
                    </p>
                  </div>
                  <span className={`fm-pill ${isUpcoming ? 'fm-pill-info' : 'fm-pill-neutral'}`}>
                    {isUpcoming
                      ? t('clients.related.upcoming', 'Upcoming')
                      : t('clients.related.past', 'Vergangen')}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="rounded-xl border border-line bg-card p-5">
        <h3 className="mb-3 flex items-center gap-2 font-display text-lg text-ink">
          <User className="h-4 w-4 text-ink-3" />
          {t('clients.related.contact_agents', 'Makler in Kontakt')} (
          {contactAgents.length})
        </h3>
        <p className="mb-3 text-xs text-ink-3">
          {t(
            'clients.related.contact_agents_hint',
            'Alle Makler, die mit diesem Klienten Termine hatten — chronologisch nach letztem Kontakt.',
          )}
        </p>
        {contactAgents.length === 0 ? (
          <p className="py-4 text-center text-sm text-ink-3">
            {t('clients.related.no_contact_agents', 'Noch kein Maklerkontakt.')}
          </p>
        ) : (
          <ul className="space-y-2">
            {contactAgents.map((a) => (
              <li
                key={a.profile_id}
                className="flex items-center gap-3 rounded-md border border-line bg-paper-2 px-3 py-2.5"
              >
                <Avatar
                  name={a.full_name}
                  src={
                    a.avatar_key
                      ? `/auth/avatar/${a.profile_id}?v=${a.last_contact_at ?? 0}`
                      : null
                  }
                  size="sm"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-ink truncate">
                    {a.full_name}
                  </p>
                  <p className="text-[11px] uppercase text-ink-3 tracking-[0.08em]">
                    {a.agent_id} ·{' '}
                    {t(`agents.role.${a.role}`, a.role)} ·{' '}
                    {a.total_appointments}{' '}
                    {t('clients.related.contact_appts', 'Termine')}
                  </p>
                </div>
                {a.last_contact_at && (
                  <span className="fm-pill fm-pill-neutral">
                    {formatDate(a.last_contact_at, {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {externalInterests.length > 0 && (
        <div className="rounded-xl border border-line bg-card p-5">
          <h3 className="mb-3 flex items-center gap-2 font-display text-lg text-ink">
            <Home className="h-4 w-4 text-ink-3" />
            {t('clients.related.external_interests', 'Externe Anzeigen (FSBO)')} (
            {externalInterests.length})
          </h3>
          <p className="mb-3 text-xs text-ink-3">
            {t(
              'clients.related.external_interests_hint',
              'Privatverkaufs-Anzeigen die du für diesen Klienten via Recherche gespeichert hast.',
            )}
          </p>
          <ul className="space-y-2">
            {externalInterests.map((e) => {
              const snap = (() => {
                try {
                  return JSON.parse(e.snapshot_json) as {
                    title?: string;
                    price_eur?: number;
                    size_m2?: number;
                    rooms?: number;
                    postal_code?: string;
                  };
                } catch {
                  return null;
                }
              })();
              return (
                <li
                  key={e.id}
                  className="flex items-center gap-3 rounded-md border border-line bg-paper-2 px-3 py-2.5"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-ink">
                      {snap?.title ?? e.external_url}
                    </p>
                    <p className="text-[11px] text-ink-3">
                      {snap?.price_eur && formatMoney(snap.price_eur)}
                      {snap?.size_m2 && ` · ${snap.size_m2} m²`}
                      {snap?.rooms && ` · ${snap.rooms} Zi`}
                      {snap?.postal_code && ` · ${snap.postal_code}`}
                      {' · '}
                      <span className="capitalize">{e.external_source}</span>
                    </p>
                  </div>
                  <a
                    href={e.external_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-semibold text-[hsl(var(--brand-gold-deep))] hover:underline"
                  >
                    {t('clients.related.external_open', 'Original')}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─────────── Tab 3: Verlauf ───────────

type HistoryFilter = 'all' | 'basics' | 'notes';

const BASIC_FIELDS = new Set([
  'first_name',
  'last_name',
  'title',
  'phone',
  'phone_2',
  'email',
  'address',
  'location',
  'platform',
  'platform_code',
  'birth_date',
  'citizenship',
  'sv_number',
  'client_type',
  'status',
]);

// ─────────── Tab 3a: Historische Notizen (timeline) ───────────
//
// Lets makers backfill paper-era follow-up records: enter a historical
// date + free-text note. Sorted newest-first. Existing notes can be
// soft-deleted (only by author or admin; backend enforces ACL via the
// /clients/:id route check).
function NotesPanel({ client }: { client: ClientRow }) {
  const { t } = useTranslation();
  const clientId = client.id;
  const { data: notes = [], isLoading } = useClientTimelineNotes(clientId);
  const create = useCreateTimelineNote(clientId);
  const remove = useDeleteTimelineNote(clientId);
  const legacyNotes = [client.notes_1, client.notes_2, client.notes_3].filter(
    (n): n is string => !!n && n.trim() !== '',
  );
  const [showAdd, setShowAdd] = useState(false);
  const [noteDate, setNoteDate] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });
  const [body, setBody] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!body.trim()) return;
    try {
      await create.mutateAsync({
        noteDate: new Date(noteDate + 'T12:00:00').getTime(),
        body: body.trim(),
      });
      setBody('');
      setShowAdd(false);
    } catch (cause) {
      setErr((cause as Error).message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-display text-base font-medium text-ink">
            {t('clients.notes.title', 'Historische Notizen')}
          </p>
          <p className="text-xs text-ink-3">
            {t(
              'clients.notes.subtitle',
              'Frühere Telefonate, Treffen, Notizen aus Papier-Ära — mit Datum eintragen.',
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowAdd((v) => !v)}
          className="rounded-md border border-line bg-card px-3 py-1.5 text-xs font-medium hover:bg-paper-2"
        >
          {showAdd
            ? t('common.cancel', 'Abbrechen')
            : t('clients.notes.add', '+ Notiz hinzufügen')}
        </button>
      </div>

      {showAdd && (
        <form
          onSubmit={submit}
          className="space-y-2 rounded-xl border border-line bg-paper-2 p-3"
        >
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-[10rem_1fr]">
            <input
              type="date"
              value={noteDate}
              onChange={(e) => setNoteDate(e.target.value)}
              className="h-9 rounded-md border border-line bg-card px-2 text-sm"
              required
            />
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={3}
              placeholder={t(
                'clients.notes.body_placeholder',
                'z.B. „Telefonat 13.03.2024 — Familie noch nicht entschieden, Rückruf in 2 Wochen."',
              )}
              className="w-full rounded-md border border-line bg-card px-2 py-1.5 text-sm"
              required
            />
          </div>
          {err && <p className="text-xs text-destructive">{err}</p>}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={create.isPending}
              className="rounded-md bg-ink px-3 py-1.5 text-xs font-semibold text-paper disabled:opacity-60"
            >
              {create.isPending
                ? t('common.saving', 'Speichern …')
                : t('common.save', 'Speichern')}
            </button>
          </div>
        </form>
      )}

      {legacyNotes.length > 0 && (
        <ul className="space-y-2">
          {legacyNotes.map((n, i) => (
            <li
              key={`legacy-${i}`}
              className="rounded-xl border border-line bg-paper-2 p-3 text-sm"
            >
              <p className="whitespace-pre-wrap text-ink-2">{n}</p>
            </li>
          ))}
        </ul>
      )}

      {isLoading ? (
        <p className="text-sm text-ink-3">{t('common.loading', 'Lädt …')}</p>
      ) : notes.length === 0 ? (
        legacyNotes.length === 0 && (
          <p className="text-sm text-ink-3">
            {t('clients.notes.empty', 'Noch keine Notizen.')}
          </p>
        )
      ) : (
        <ul className="space-y-2">
          {notes.map((n) => (
            <li
              key={n.id}
              className="rounded-xl border border-line bg-card p-3 text-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <p className="text-xs font-medium text-ink-3">
                    {formatDate(new Date(n.note_date), {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </p>
                  <p className="whitespace-pre-wrap text-ink">{n.body}</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(t('clients.notes.delete_confirm', 'Notiz löschen?'))) {
                      remove.mutateAsync(n.id);
                    }
                  }}
                  className="text-xs text-ink-3 hover:text-destructive"
                  title={t('common.delete', 'Löschen')}
                >
                  ×
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function HistoryPanel({ client }: { client: ClientRow }) {
  const { t } = useTranslation();
  const { data: history = [], isLoading } = useClientHistory(client.id);
  const { data: agents = [] } = useAgents();
  const [filter, setFilter] = useState<HistoryFilter>('all');

  const userIdToAgent = useMemo(() => {
    const map = new Map<string, { full_name: string; agent_id: string }>();
    for (const a of agents) {
      map.set(a.user_id, { full_name: a.full_name, agent_id: a.agent_id });
    }
    return map;
  }, [agents]);
  // Separate index by profile.id — clients.assigned_agent_id stores
  // profile IDs, not user IDs.
  const profileIdToAgent = useMemo(() => {
    const map = new Map<string, { full_name: string; agent_id: string }>();
    for (const a of agents) {
      map.set(a.id, { full_name: a.full_name, agent_id: a.agent_id });
    }
    return map;
  }, [agents]);
  const assignedMaker = client.assigned_agent_id
    ? profileIdToAgent.get(client.assigned_agent_id) ?? null
    : null;

  // Created/Last Modified inferred from history (first/most-recent rows).
  const createdRow = history[history.length - 1];
  const lastRow = history[0];
  const createdAt = createdRow?.created_at ?? client.created_at;
  const updatedAt = lastRow?.created_at ?? client.updated_at;
  const createdBy = createdRow?.changed_by ?? client.created_by;
  const updatedBy = lastRow?.changed_by ?? null;

  const filtered = useMemo(() => {
    if (filter === 'all') return history;
    if (filter === 'notes')
      return history.filter((h) => h.field_changed?.startsWith('notes'));
    if (filter === 'basics')
      return history.filter(
        (h) => h.field_changed && BASIC_FIELDS.has(h.field_changed),
      );
    return history;
  }, [history, filter]);

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-line bg-card p-5">
        <h3 className="mb-3 font-display text-lg text-ink">
          {t('clients.history.record_info', 'Record Information')}
        </h3>
        <div className="grid gap-4 md:grid-cols-2">
          <RecordSummary
            label={t('clients.history.assigned_agent', 'Zuständiger Makler')}
            user={assignedMaker}
            at={null}
            highlight
          />
          <RecordSummary
            label={t('clients.history.created_by', 'Created By')}
            user={createdBy ? userIdToAgent.get(createdBy) ?? null : null}
            at={createdAt}
          />
          <RecordSummary
            label={t('clients.history.modified_by', 'Last Modified By')}
            user={updatedBy ? userIdToAgent.get(updatedBy) ?? null : null}
            at={updatedAt}
          />
        </div>
      </div>

      <div className="rounded-xl border border-line bg-card p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-display text-lg text-ink">
            <History className="h-4 w-4 text-ink-3" />
            {t('clients.history.title', 'Kunden Historie')}
          </h3>
        </div>

        <div className="mb-4 flex gap-1 overflow-x-auto rounded-md bg-paper-2 p-1 scrollbar-thin md:grid md:grid-cols-3">
          <FilterButton active={filter === 'all'} onClick={() => setFilter('all')}>
            {t('clients.history.filter_all', 'Alle Änderungen')}
          </FilterButton>
          <FilterButton active={filter === 'basics'} onClick={() => setFilter('basics')}>
            {t('clients.history.filter_basics', 'Grundinformationen')}
          </FilterButton>
          <FilterButton active={filter === 'notes'} onClick={() => setFilter('notes')}>
            {t('clients.history.filter_notes', 'Notizen')}
          </FilterButton>
        </div>

        {isLoading ? (
          <p className="py-4 text-center text-sm text-ink-3">
            {t('common.loading', 'Lädt …')}
          </p>
        ) : filtered.length === 0 ? (
          <p className="py-4 text-center text-sm text-ink-3">
            {t('clients.history.empty', 'Keine Historie verfügbar')}
          </p>
        ) : (
          <ul className="space-y-3">
            {filtered.map((h) => (
              <HistoryRow key={h.id} row={h} userMap={userIdToAgent} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function HistoryRow({
  row,
  userMap,
}: {
  row: ClientHistoryRow;
  userMap: Map<string, { full_name: string; agent_id: string }>;
}) {
  const { t } = useTranslation();
  const user = row.changed_by ? userMap.get(row.changed_by) : null;
  const isCreated = row.action === 'created';
  return (
    <li className="border-s-2 border-line-strong ps-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Avatar name={user?.full_name ?? '?'} size="sm" />
          <span className="truncate text-sm font-medium text-ink">
            {user?.full_name ?? row.changed_by ?? '—'}
          </span>
          {user?.agent_id && (
            <span className="font-mono text-[11px] uppercase rounded-full border border-line px-2 py-0.5 text-ink-3">
              {user.agent_id}
            </span>
          )}
        </div>
        <p className="flex-shrink-0 text-xs text-ink-3">
          {formatDateTime(new Date(row.created_at))}
        </p>
      </div>
      <div className="mt-1 ms-9">
        {isCreated ? (
          <span className="fm-pill fm-pill-info">
            {t('clients.history.created', 'Kunde Erstellt')}
          </span>
        ) : (
          <>
            <p className="text-xs text-ink-3">{fieldLabel('client', row.field_changed)}</p>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
              {row.old_value && (
                <span className="rounded-md border border-line px-2 py-0.5 text-ink-3 line-through">
                  {valueLabel('client', row.field_changed, row.old_value, t)}
                </span>
              )}
              {row.old_value && row.new_value && <span className="text-ink-3">→</span>}
              {row.new_value && (
                <span className="rounded-md bg-[hsl(var(--info-bg))] px-2 py-0.5 font-medium text-[hsl(var(--info))]">
                  {valueLabel('client', row.field_changed, row.new_value, t)}
                </span>
              )}
            </div>
            {row.change_reason && (
              <p className="mt-1 text-[11px] italic text-ink-3">
                {t('clients.history.reason', 'Grund:')}{' '}
                {formatChangeReason(row.change_reason, t)}
              </p>
            )}
          </>
        )}
      </div>
    </li>
  );
}

// Turns machine change_reason slugs into readable text; extension reasons
// are stored as `claim_extended: <operator text>` — surface the operator
// text with a readable prefix.
function formatChangeReason(
  reason: string,
  t: (k: string, d: string) => string,
): string {
  if (reason.startsWith('claim_extended:')) {
    return `${t('clients.history.reason_extended', 'Frist verlängert')} — ${reason.slice('claim_extended:'.length).trim()}`;
  }
  if (reason.startsWith('client_closed:')) {
    return `${t('clients.history.reason_closed', 'Verkäufer geschlossen')} — ${reason.slice('client_closed:'.length).trim()}`;
  }
  const map: Record<string, string> = {
    vermittlungsauftrag_signed: t('clients.history.reason_signed', 'Vermittlungsauftrag unterschrieben'),
    claim_expired: t('clients.history.reason_expired', '30-Tage-Frist abgelaufen'),
    client_claim: t('clients.history.reason_claim', 'Klient übernommen'),
    client_claim_delegated: t('clients.history.reason_delegated', 'Klient zugewiesen'),
    kundenpool_claim: t('clients.history.reason_pool', 'Aus dem Kundenpool übernommen'),
    client_reopened: t('clients.history.reason_reopened', 'Verkäufer wieder geöffnet'),
  };
  return map[reason] ?? reason;
}

// ─────────── Helpers ───────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-line bg-card p-5">
      <h3 className="mb-3 font-display text-lg text-ink">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <p className="text-xs uppercase tracking-[0.08em] text-ink-3">{label}</p>
      <p className="text-sm text-ink">{value}</p>
    </div>
  );
}

function FilterButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-shrink-0 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-semibold transition ${
        active ? 'bg-card text-ink shadow-soft' : 'text-ink-3 hover:text-ink'
      }`}
    >
      {children}
    </button>
  );
}

function RecordSummary({
  label,
  user,
  at,
  highlight = false,
}: {
  label: string;
  user: { full_name: string; agent_id: string } | null;
  at: number | null;
  highlight?: boolean;
}) {
  return (
    <div className={highlight ? 'rounded-lg border border-[hsl(var(--brand-gold))]/40 bg-[hsl(var(--brand-gold))]/5 p-3' : ''}>
      <p className="text-xs uppercase tracking-[0.08em] text-ink-3">{label}</p>
      <p className={`mt-1 text-sm font-medium text-ink ${highlight ? 'text-base' : ''}`}>
        {user?.full_name ?? '—'}
      </p>
      {user?.agent_id && (
        <p className="text-xs text-ink-3">
          Agent ID: <span className="font-mono">{user.agent_id}</span>
        </p>
      )}
      {at != null && (
        <p className="mt-1 inline-flex items-center gap-1 text-xs text-ink-3">
          <StickyNote className="h-3 w-3" />
          {formatDateTime(new Date(at))}
        </p>
      )}
    </div>
  );
}


// ─────────── Kundenpool claim banner ───────────
//
// Shown for an unclaimed Kundenpool client (in place of the lock banner).
// Any maker/CC may take it for themselves or assign it to another user;
// either action graduates the client into the normal pool and arms the
// 30-day lock for the new owner. The dialog's useClient query refetches on
// success, so the banner flips to the normal LockBanner automatically.
function PoolClaimBanner({ client }: { client: ClientRow }) {
  const { t } = useTranslation();
  const claimPool = useClaimPoolClient();
  const [delegateOpen, setDelegateOpen] = useState(false);
  const fullName = [client.title, client.first_name, client.last_name]
    .filter(Boolean)
    .join(' ');

  const claimSelf = () => {
    claimPool.mutate(
      { id: client.id },
      {
        onSuccess: () =>
          toast.success(t('clients.pool.claimed_self', '„{{name}}" übernommen.', { name: fullName })),
        onError: (e) => toast.error((e as Error).message),
      },
    );
  };

  return (
    <div className="rounded-lg border border-[hsl(var(--brand-gold))]/40 bg-[hsl(var(--brand-gold))]/5 px-3 py-2 text-xs flex items-center gap-2 flex-wrap">
      <Users className="h-3.5 w-3.5 text-[hsl(var(--brand-gold-deep))]" />
      <span className="font-medium text-ink">
        {t('clients.pool.banner', 'Aus dem Kundenpool — frei zum Übernehmen')}
      </span>
      <div className="ms-auto flex items-center gap-2">
        <button
          type="button"
          onClick={claimSelf}
          disabled={claimPool.isPending}
          className="inline-flex items-center gap-1 rounded-md border border-[hsl(var(--brand-gold))]/60 bg-[hsl(var(--brand-gold))]/10 px-2 py-1 text-[11px] font-semibold text-[hsl(var(--brand-gold-deep))] hover:bg-[hsl(var(--brand-gold))]/20 disabled:opacity-50"
        >
          <UserPlus className="h-3 w-3" />
          {t('clients.pool.claim_self', 'Mir zuweisen')}
        </button>
        <button
          type="button"
          onClick={() => setDelegateOpen(true)}
          disabled={claimPool.isPending}
          className="inline-flex items-center gap-1 rounded-md border border-line px-2 py-1 text-[11px] font-semibold text-ink hover:bg-paper-2 disabled:opacity-50"
        >
          <UserCog className="h-3 w-3" />
          {t('clients.pool.delegate', 'Zuweisen an …')}
        </button>
      </div>
      {delegateOpen && (
        <PoolDelegateDialog
          open={delegateOpen}
          onOpenChange={setDelegateOpen}
          busy={claimPool.isPending}
          onSelect={(profileId) =>
            claimPool.mutate(
              { id: client.id, assignToProfileId: profileId },
              {
                onSuccess: () => {
                  setDelegateOpen(false);
                  toast.success(
                    t('clients.pool.delegated', '„{{name}}" zugewiesen.', { name: fullName }),
                  );
                },
                onError: (e) => toast.error((e as Error).message),
              },
            )
          }
        />
      )}
    </div>
  );
}

// ─────────── Responsibility banner (batch BE — no time limit) ───────────
//
// Seller lifecycle: FREI → ÜBERNOMMEN (responsible makler, takeover by any
// other makler possible ANYTIME) → either VERTRAG UNTERSCHRIEBEN (exclusive,
// final) or GESCHLOSSEN (sold elsewhere / withdrew; who+when+why recorded).
// Non-seller clients keep the legacy 30-day lock display (Kundenpool).
function LockBanner({ client }: { client: ClientRow }) {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const { data: agents = [] } = useAgents();
  const claimGeneral = useClaimPoolClient();
  const assignSecondary = useAssignSecondaryAgent(client.id);
  const removeSecondary = useRemoveSecondaryAgent(client.id);
  const [delegateOpen, setDelegateOpen] = useState(false);
  const [secondaryOpen, setSecondaryOpen] = useState(false);

  const myProfileId = profile?.id ?? null;
  const isAdmin = profile?.role === 'admin';
  const now = Date.now();
  // Documented pitfall: timestamp_ms columns serialize as ISO STRINGS even
  // though the type says number — parse defensively before comparing.
  const toMs = (v: number | string | null | undefined): number | null =>
    v == null ? null : typeof v === 'number' ? v : new Date(v).getTime();
  const lockUntil = toMs(client.aufnahme_lock_until);
  const lockActive = lockUntil != null && !Number.isNaN(lockUntil) && lockUntil > now;
  const daysLeft = lockActive ? Math.max(0, Math.ceil((lockUntil! - now) / 86_400_000)) : 0;

  const ownerName = client.assigned_agent_id
    ? (agents.find((a) => a.id === client.assigned_agent_id)?.full_name ?? '—')
    : null;
  const iAmOwner = client.assigned_agent_id === myProfileId;
  const isSellerClient =
    client.client_type === 'prodavac' || client.client_type === 'najmodavac';

  // ── Seller-rules batch: second makler + activity compliance ──
  const secondaryName = client.secondary_agent_id
    ? (agents.find((a) => a.id === client.secondary_agent_id)?.full_name ?? '—')
    : null;
  const iAmSecondary =
    client.secondary_agent_id != null && client.secondary_agent_id === myProfileId;
  // Compliance manager = admin with profiles.seller_compliance OR the
  // super admin (admin1). Mirrors backend isComplianceManager().
  const isComplianceAdmin =
    isAdmin &&
    (profile?.seller_compliance === true || profile?.agent_id === 'admin1');
  const isSigned = client.status === 'potpisan_ugovor';
  // Any admin manages the secondary on UNSIGNED sellers; on signed (AV)
  // sellers only compliance managers (backend 403 compliance_manager_only).
  const canManageSecondary = isSellerClient && (isSigned ? isComplianceAdmin : isAdmin);
  // Activity-compliance deadline (armed at contract signing).
  const dueMs = toMs(client.activity_due_at);
  const dueValid = dueMs != null && !Number.isNaN(dueMs);
  const dueOverdue = dueValid && dueMs! <= now;
  const canSeeCompliance = iAmOwner || iAmSecondary || isComplianceAdmin;

  const errFallback = t('errors.general', 'Die Aktion ist fehlgeschlagen.');
  const onRemoveSecondary = () => {
    if (
      !window.confirm(
        t('clients.lock.remove_secondary_confirm', '{{name}} als zweiten Makler wirklich entfernen?', {
          name: secondaryName ?? '—',
        }),
      )
    )
      return;
    removeSecondary.mutate(undefined, {
      onSuccess: () =>
        toast.success(t('clients.lock.secondary_removed', 'Zweiter Makler entfernt.')),
      onError: (e) => toast.error(humanizeApiError((e as Error).message, t, errFallback)),
    });
  };

  const secondaryControls = canManageSecondary ? (
    client.secondary_agent_id ? (
      <button
        type="button"
        onClick={onRemoveSecondary}
        disabled={removeSecondary.isPending}
        className="inline-flex items-center gap-1 rounded-md border border-line px-2 py-1 text-[11px] font-semibold text-ink hover:bg-paper-2 disabled:opacity-50"
      >
        {t('clients.lock.remove_secondary', 'Zweiten Makler entfernen')}
      </button>
    ) : (
      <button
        type="button"
        onClick={() => setSecondaryOpen(true)}
        disabled={assignSecondary.isPending}
        className="inline-flex items-center gap-1 rounded-md border border-line px-2 py-1 text-[11px] font-semibold text-ink hover:bg-paper-2 disabled:opacity-50"
      >
        {t('clients.lock.assign_secondary', 'Zweiten Makler zuweisen')}
      </button>
    )
  ) : null;

  const secondaryDialog = secondaryOpen ? (
    <AssignSecondaryAgentDialog
      open={secondaryOpen}
      onOpenChange={setSecondaryOpen}
      busy={assignSecondary.isPending}
      excludeProfileId={client.assigned_agent_id}
      onSelect={(profileId) =>
        assignSecondary.mutate(profileId, {
          onSuccess: () => {
            setSecondaryOpen(false);
            toast.success(
              t('clients.lock.secondary_assigned', 'Zweiter Makler zugewiesen.'),
            );
          },
          onError: (e) =>
            toast.error(humanizeApiError((e as Error).message, t, errFallback)),
        })
      }
    />
  ) : null;

  const complianceLine =
    isSigned && dueValid && canSeeCompliance ? (
      <p
        className={
          dueOverdue
            ? 'w-full text-[11px] font-semibold text-destructive'
            : 'w-full text-[11px] text-ink-3'
        }
      >
        {dueOverdue
          ? t('clients.compliance.overdue', 'Aktivität überfällig — war fällig am {{date}}', {
              date: formatDate(new Date(dueMs!)),
            })
          : t('clients.compliance.due', 'Nächste Aktivität fällig: {{date}}', {
              date: formatDate(new Date(dueMs!)),
            })}
      </p>
    ) : null;

  // ── Final state 1: contract signed (exclusive, permanent) ──
  // Seller-rules batch: shows the secondary makler, the compliance
  // deadline (owners + compliance admins) and the compliance-manager-only
  // assign/remove controls for the second makler.
  if (client.status === 'potpisan_ugovor') {
    return (
      <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 px-3 py-2 text-xs flex items-center gap-2 flex-wrap">
        <span aria-hidden>🤝</span>
        <span className="font-medium text-emerald-700 dark:text-emerald-400">
          {t('clients.lock.signed', 'Vertrag unterschrieben — zuständig:')}{' '}
          <b>{ownerName}</b>
          {secondaryName && (
            <>
              {' · '}
              {t('clients.lock.secondary_label', 'Zweiter Makler: {{name}}', {
                name: secondaryName,
              })}
            </>
          )}
          .
        </span>
        {secondaryControls && (
          <div className="ms-auto flex items-center gap-2">{secondaryControls}</div>
        )}
        {complianceLine}
        {secondaryDialog}
      </div>
    );
  }

  // ── Final state 2: seller closed (who / when / why) ──
  if (client.status === 'zatvoreno') {
    return <ClosedBanner client={client} isAdmin={isAdmin} />;
  }

  const claimSelf = () => {
    claimGeneral.mutate(
      { id: client.id },
      {
        onSuccess: () =>
          toast.success(t('clients.lock.claimed_open', 'Sie sind jetzt zuständig für den Klienten.')),
        onError: (e) => toast.error((e as Error).message),
      },
    );
  };

  // ── Seller states (batch BE): no countdown, takeover anytime ──
  if (isSellerClient) {
    const canTakeover = !!myProfileId && !iAmOwner && profile?.role === 'makler';
    const canDelegate = isAdmin || profile?.role === 'call_center';
    return (
      <div
        className={
          client.assigned_agent_id
            ? 'rounded-lg border border-[hsl(var(--info)/0.4)] bg-[hsl(var(--info-bg))] px-3 py-2 text-xs flex items-center gap-2 flex-wrap'
            : 'rounded-lg border border-emerald-500/40 bg-emerald-500/5 px-3 py-2 text-xs flex items-center gap-2 flex-wrap'
        }
      >
        <span aria-hidden>{client.assigned_agent_id ? '👤' : '🔓'}</span>
        {client.assigned_agent_id ? (
          <span className="font-medium text-[hsl(var(--info))]">
            {iAmOwner
              ? t('clients.lock.mine_open', 'Sie sind zuständig für diesen Verkäufer.')
              : t('clients.lock.other_open', 'Zuständig: {{maker}}', { maker: ownerName ?? '—' })}
            {secondaryName && (
              <>
                {' · '}
                {t('clients.lock.secondary_label', 'Zweiter Makler: {{name}}', {
                  name: secondaryName,
                })}
              </>
            )}
          </span>
        ) : (
          <span className="font-medium text-emerald-700 dark:text-emerald-400">
            {t('clients.lock.free', 'Frei — übernehmbar')}
          </span>
        )}
        <div className="ms-auto flex items-center gap-2">
          {canTakeover && (
            <button
              type="button"
              onClick={() => {
                if (
                  client.assigned_agent_id &&
                  !window.confirm(
                    t('clients.lock.takeover_confirm', {
                      defaultValue:
                        'Dieser Verkäufer ist aktuell bei {{maker}}. Wirklich übernehmen? {{maker}} und die anderen Makler werden benachrichtigt.',
                      maker: ownerName ?? '—',
                    }),
                  )
                )
                  return;
                claimSelf();
              }}
              disabled={claimGeneral.isPending}
              className="inline-flex items-center gap-1 rounded-md border border-emerald-500/60 bg-emerald-500/10 px-2 py-1 text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/20 disabled:opacity-50"
            >
              {claimGeneral.isPending
                ? t('clients.lock.claiming', 'Wird übernommen …')
                : t('clients.lock.claim', 'Übernehmen')}
            </button>
          )}
          {canDelegate && (
            <button
              type="button"
              onClick={() => setDelegateOpen(true)}
              disabled={claimGeneral.isPending}
              className="inline-flex items-center gap-1 rounded-md border border-line px-2 py-1 text-[11px] font-semibold text-ink hover:bg-paper-2 disabled:opacity-50"
            >
              {t('clients.pool.delegate', 'Zuweisen an …')}
            </button>
          )}
          {secondaryControls}
          <CloseSellerButton client={client} />
        </div>
        {delegateOpen && (
          <PoolDelegateDialog
            open={delegateOpen}
            onOpenChange={setDelegateOpen}
            busy={claimGeneral.isPending}
            onSelect={(profileId) =>
              claimGeneral.mutate(
                { id: client.id, assignToProfileId: profileId },
                {
                  onSuccess: () => {
                    setDelegateOpen(false);
                    toast.success(t('clients.lock.delegated_open', 'Klient zugewiesen.'));
                  },
                  onError: (e) => toast.error((e as Error).message),
                },
              )
            }
          />
        )}
        {secondaryDialog}
      </div>
    );
  }

  // ── Legacy (non-seller / Kundenpool) lock display — unchanged rules ──
  const holder = client.aufnahme_lock_agent_id
    ? agents.find((a) => a.id === client.aufnahme_lock_agent_id)
    : null;
  const iAmHolder = client.aufnahme_lock_agent_id === myProfileId;
  if (lockActive && iAmHolder) {
    return (
      <div className="rounded-lg border border-[hsl(var(--brand-gold))]/40 bg-[hsl(var(--brand-gold))]/5 px-3 py-2 text-xs flex items-center gap-2">
        <span aria-hidden>🔒</span>
        <span className="font-medium text-ink">
          {t('clients.lock.mine', 'Noch {{n}} Tage als zuständiger Makler (Übernahme-Sperre)', { n: daysLeft })}
        </span>
      </div>
    );
  }
  if (lockActive && !iAmHolder) {
    return (
      <div className="rounded-lg border border-[hsl(var(--info)/0.4)] bg-[hsl(var(--info-bg))] px-3 py-2 text-xs flex items-center gap-2">
        <span aria-hidden>👤</span>
        <span className="font-medium text-[hsl(var(--info))]">
          {t('clients.lock.other', 'Zuständig: {{maker}} (Übernahme in {{n}} Tagen möglich)', {
            n: daysLeft,
            maker: holder?.full_name ?? '—',
          })}
        </span>
      </div>
    );
  }
  const canClaim = !!myProfileId && !iAmOwner && profile?.role === 'makler';
  return (
    <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 px-3 py-2 text-xs flex items-center gap-2 flex-wrap">
      <span aria-hidden>🔓</span>
      <span className="font-medium text-emerald-700 dark:text-emerald-400">
        {t('clients.lock.free', 'Frei — übernehmbar')}
      </span>
      {canClaim && (
        <button
          type="button"
          onClick={claimSelf}
          disabled={claimGeneral.isPending}
          className="ms-auto inline-flex items-center gap-1 rounded-md border border-emerald-500/60 bg-emerald-500/10 px-2 py-1 text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/20 disabled:opacity-50"
        >
          {claimGeneral.isPending
            ? t('clients.lock.claiming', 'Wird übernommen …')
            : t('clients.lock.claim', 'Übernehmen')}
        </button>
      )}
      {isAdmin && client.assigned_agent_id && (
        <span className="ms-auto text-[10px] text-ink-3">
          {t('clients.lock.admin_owner_hint', 'Aktueller Makler: {{maker}}', {
            maker: ownerName ?? '—',
          })}
        </span>
      )}
    </div>
  );
}

// ─────────── Closed-seller banner: who / when / why + admin reopen ───────────
function ClosedBanner({ client, isAdmin }: { client: ClientRow; isAdmin: boolean }) {
  const { t } = useTranslation();
  const { data: agents = [] } = useAgents();
  const reopen = useReopenClient();
  const closedAtMs =
    client.closed_at == null
      ? null
      : typeof client.closed_at === 'number'
        ? client.closed_at
        : new Date(client.closed_at).getTime();
  // closed_by is a users.id — resolve via the agents list's user_id field.
  const closer = client.closed_by
    ? agents.find((a) => a.user_id === client.closed_by)
    : null;
  return (
    <div className="rounded-lg border border-line-strong bg-paper-2 px-3 py-2 text-xs flex items-start gap-2 flex-wrap">
      <span aria-hidden>⛔</span>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-ink">
          {t('clients.close.banner_title', 'Verkäufer geschlossen')}
          {closedAtMs && !Number.isNaN(closedAtMs) && (
            <span className="text-ink-3 font-normal">
              {' '}
              · {formatDateTime(new Date(closedAtMs))}
            </span>
          )}
          {closer && (
            <span className="text-ink-3 font-normal">
              {' '}
              · {t('clients.close.banner_by', 'von')} {closer.full_name}
            </span>
          )}
        </p>
        {client.closed_reason && (
          <p className="mt-0.5 text-ink-2 italic">
            {t('clients.close.banner_reason', 'Grund:')} {client.closed_reason}
          </p>
        )}
      </div>
      {isAdmin && (
        <button
          type="button"
          onClick={() => {
            if (!window.confirm(t('clients.close.reopen_confirm', 'Verkäufer wieder öffnen?'))) return;
            reopen.mutate(client.id, {
              onSuccess: () => toast.success(t('clients.close.reopened', 'Verkäufer wieder geöffnet.')),
              onError: (e) => toast.error((e as Error).message),
            });
          }}
          disabled={reopen.isPending}
          className="rounded-md border border-line px-2 py-1 text-[11px] font-semibold text-ink hover:bg-paper disabled:opacity-50"
        >
          {t('clients.close.reopen', 'Wieder öffnen')}
        </button>
      )}
    </div>
  );
}

// ─────────── Close-seller button (opens shared CloseSellerDialog) ───────────
function CloseSellerButton({ client }: { client: ClientRow }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 rounded-md border border-red-300/60 bg-red-500/5 px-2 py-1 text-[11px] font-semibold text-red-700 dark:text-red-400 hover:bg-red-500/10"
      >
        {t('clients.close.button', 'Schließen')}
      </button>
      {open && <CloseSellerDialog client={client} open={open} onOpenChange={setOpen} />}
    </>
  );
}
